import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ClickAwayListener, Popper } from '@material-ui/core';

import { ReactComponent as Location } from 'assets/icons/google-location.svg';
import { Asterix } from 'components/Form/HelperText/HelperText';
import TextInput from 'components/Form/Inputs/TextInput/TextInput';
import {
  setFamilyBookingHomeAddress,
  setFamilyBookingPostalCode,
} from 'redux/familyBookingSlice';
import { useAppDispatch, useAppSelector } from 'redux/reduxHooks';

import styles from './HomeAddressFields.module.scss';

const PLACES_AUTOCOMPLETE_URL =
  'https://places.googleapis.com/v1/places:autocomplete';

const PLACES_DETAILS_URL = 'https://places.googleapis.com/v1/places';

const AUTOCOMPLETE_DEBOUNCE_MS = 300;

interface GooglePrediction {
  placeId: string;
  text: {
    text: string;
  };
  structuredFormat: {
    mainText: {
      text: string;
    };
    secondaryText: {
      text: string;
    };
  };
}

interface GooglePlaceAddressDetail {
  postalAddress?: {
    postalCode?: string;
  };
}

interface GoogleSuggestions {
  suggestions: { placePrediction: GooglePrediction }[];
}

const HomeAddressFields = () => {
  const dispatch = useAppDispatch();

  const homeAddress = useAppSelector(
    (state) => state.familyBookingSlice.homeAddress
  );

  const postalCode = useAppSelector(
    (state) => state.familyBookingSlice.postalCode
  );

  const [options, setOptions] = useState<GooglePrediction[]>([]);

  const [option, setOption] = useState<GooglePrediction | null>(null);

  const [isOpen, setIsOpen] = useState(false);

  const elementRef = useRef<HTMLInputElement | null>(null);

  const isPopperOpen = isOpen && options.length > 0;

  const handleHomeAddressChange = (newValue: string) => {
    dispatch(setFamilyBookingHomeAddress(newValue));

    setIsOpen(true);

    setOption(null);
  };

  const handlePostalCodeChange = (newValue: string) => {
    dispatch(setFamilyBookingPostalCode(newValue));
  };

  const handleOptionSelect = async (selectedOption: GooglePrediction) => {
    setOption(selectedOption);

    setOptions([]);

    dispatch(setFamilyBookingHomeAddress(selectedOption.text.text));

    setIsOpen(false);

    try {
      const result = await axios.get<GooglePlaceAddressDetail>(
        `${PLACES_DETAILS_URL}/${selectedOption.placeId}`,
        {
          headers: {
            'X-Goog-Api-Key': process.env.REACT_APP_GOOGLE_MAP_API_KEY,
            'X-Goog-FieldMask': 'postalAddress',
          },
        }
      );

      dispatch(
        setFamilyBookingPostalCode(
          result.data?.postalAddress?.postalCode ?? ''
        )
      );
    } catch (error) {
      dispatch(setFamilyBookingPostalCode(''));
    }
  };

  useEffect(() => {
    const axiosToken = axios.CancelToken.source();

    //--A picked suggestion must not re-query and reopen the list--
    if (option) return;

    setOptions([]);

    const timer = setTimeout(async () => {
      if (!homeAddress) return;

      try {
        const result = await axios.post<GoogleSuggestions>(
          PLACES_AUTOCOMPLETE_URL,
          {
            input: homeAddress,
          },
          {
            headers: {
              'X-Goog-Api-Key': process.env.REACT_APP_GOOGLE_MAP_API_KEY,
            },
            cancelToken: axiosToken.token,
          }
        );

        if (!result.data) return;

        const predictions = result.data.suggestions.map(
          (suggestion) => suggestion.placePrediction
        );

        setOptions(predictions);
      } catch (error) {}
    }, AUTOCOMPLETE_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);

      axiosToken.cancel();
    };
  }, [option, homeAddress]);

  return (
    <div className={styles['address-wrapper']}>
      <div className={styles['address-column']}>
        <div className={styles['field-label']}>
          Home Address <Asterix error={'required'} />
        </div>

        <TextInput
          placeHolder={'Input Address'}
          value={homeAddress}
          onValueChanged={handleHomeAddressChange}
          ref={elementRef}
        />

        <ClickAwayListener onClickAway={() => setIsOpen(false)}>
          <Popper
            open={isPopperOpen}
            anchorEl={elementRef.current}
            placement="bottom-start"
            style={{ zIndex: 9999 }}
          >
            <div
              style={{
                width: homeAddress ? elementRef.current?.clientWidth : 0,
              }}
              className={styles['popper-content']}
            >
              {options.map((item) => (
                <div
                  className={styles['popper-item']}
                  key={item.text.text}
                  onClick={() => handleOptionSelect(item)}
                >
                  <div className={styles['icon-wrapper']}>
                    <Location className={styles['icon']} />
                  </div>

                  <div className={styles['text-wrapper']}>
                    <span>{item.structuredFormat.mainText.text}</span>

                    {item.structuredFormat.secondaryText && (
                      <span>{item.structuredFormat.secondaryText.text}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Popper>
        </ClickAwayListener>
      </div>

      <div
        className={styles['postal-code-column']}
        onFocus={() => setIsOpen(false)}
      >
        <div className={styles['field-label']}>
          ZIP/ Postal Code <Asterix error={'required'} />
        </div>

        <TextInput
          placeHolder={'Input ZIP/ Postal Code'}
          value={postalCode}
          onValueChanged={handlePostalCodeChange}
        />
      </div>
    </div>
  );
};

export default HomeAddressFields;
